import {createBrowserHistory} from 'history';
import {batch} from 'react-redux';
import store from './redux/store';
import {track} from './actions/mixpanel';
import {getCurrentDeck, getCurrentInvite} from './selectors';
import {logSlideView as logSlideViewToTycho} from './lib/tycho';
import {
  getCurrentURL,
  getNumericParam,
  isInvite,
  isSignage
} from './utils/url';
import queryParams from './queryParams';
import {deviceType} from './DeviceTypeContext.jsx';
import {SET_SEARCH_PARAMS} from './actions/ActionTypes';

const history = createBrowserHistory();
const paramNames = Object.keys(queryParams);

const maxLogSlideViewRetries = 10;
const logSlideViewRetryInterval = 1000;
const logSlideViewDelay = 2000;

let slideViewTimeout;

function logSlideView(slideIndexToLog, retryAttempts = 0) {
  const state = store.getState();
  const invite = getCurrentInvite(state);
  const deck = getCurrentDeck(state);

  if (!invite.invite_id || !deck.id) {
    if (retryAttempts < maxLogSlideViewRetries) {
      setTimeout(() => logSlideView(slideIndexToLog, retryAttempts + 1), logSlideViewRetryInterval);
    } else {
      // eslint-disable-next-line no-console
      console.warn(`Too many retries logging slide view for slide index ${slideIndexToLog}.`);
    }
    return;
  }

  const {slide_order} = deck;
  const slideId = slide_order[slideIndexToLog];

  if (!slideId) {
    // eslint-disable-next-line no-console
    console.warn(`Could not find slide index ${slideIndexToLog} to log view for.`);
    return;
  }

  logSlideViewToTycho(invite.invite_id, slideId);

  store.dispatch(track('Slide View', {
    deckId: deck.id,
    inviteId: invite.invite_id,
    inviteeId: invite.invitee_id,
    inviteeUserId: invite.invitee_user_id,
    numberOfSlides: slide_order.length,
    slideNumber: parseInt(slideIndexToLog, 10) + 1
  }));
}

function scheduleLogSlideView(changedParams) {
  if (changedParams.slideIndex === undefined) {
    return;
  }

  // Clear any pending slide view logs because the user must have quickly
  // navigated to another slide (i.e. before the timeout finished), so it
  // shouldn't be counted as a slide view.
  if (slideViewTimeout !== undefined) {
    clearTimeout(slideViewTimeout);
    slideViewTimeout = undefined;
  }

  slideViewTimeout = setTimeout(() => logSlideView(changedParams.slideIndex), logSlideViewDelay);
}

function parseParamsFromUrl(location, useDefaultValues) {
  const searchParams = new URLSearchParams(location.search);

  return paramNames.reduce((memo, key) => {
    const {
      defaultValue,
      fallback,
      type,
    } = queryParams[key];

    let value;

    if (type === Number) {
      value = getNumericParam(location.search, key, fallback);
    } else {
      value = searchParams.get(key);
    }

    memo[key] = useDefaultValues ? (value || defaultValue) : value;

    return memo;
  }, {});
}

function getParamsFromState(state) {
  return paramNames.reduce((memo, key) => {
    const paramSpec = queryParams[key];
    const isApplicableIsFn = typeof paramSpec.isApplicable === 'function';
    const isApplicable = isApplicableIsFn ? paramSpec.isApplicable() : paramSpec.isApplicable;

    if (isApplicable) {
      memo[key] = queryParams[key].selector(state);
    }

    return memo;
  }, {});
}

/**
 * Compares all applicable query parameters and determines which values have
 * changed in the new set vs the old.
 *
 * newParams - the latest parameters
 * oldParams - the previous parameters to compare against
 * destination - either "url" or "state"
 */
function getChangedParams(newParams, oldParams, destination) {
  return paramNames.reduce((memo, key) => {
    const paramSpec = queryParams[key];
    const writeToUrl = paramSpec.writeToUrl !== false;
    const isApplicable = paramSpec.isApplicable();

    if (!isApplicable) {
      return memo;
    }

    // If the changed params are being computed for the purpose of deciding
    // what to write to the URL, then omit any params that shouldn't be
    // written back to the URL.
    if (destination === 'url' && !writeToUrl) {
      return memo;
    }

    const newValue = newParams[key];
    const oldValue = oldParams[key];

    const isUndefined = newValue === undefined && oldValue === undefined;
    const hasChanged = newValue !== oldValue;

    if (hasChanged && !isUndefined) {
      memo[key] = newValue;
    }

    return memo;
  }, {});
}

function updateStateFromUrl(location) {
  const state = store.getState();
  const stateParams = getParamsFromState(state);
  const parsedUrlParams = parseParamsFromUrl(location, true);

  // Determine which parameters are different from their values in state.
  const changedParams = getChangedParams(parsedUrlParams, stateParams, 'state');

  // Nothing to do if state already has the same values.
  if (!Object.keys(changedParams).length) {
    return;
  }

  if (isInvite() || isSignage()) {
    scheduleLogSlideView(changedParams);
  }

  // Update state to be in sync with the query parameter values in the URL.
  batch(() => {
    const {deckIndex, ...rest} = changedParams;

    if (deckIndex !== undefined) {
      store.dispatch({
        type: SET_SEARCH_PARAMS,
        payload: {deckIndex}
      });
    }

    store.dispatch({
      type: SET_SEARCH_PARAMS,
      payload: rest
    });
  });
}

function updateUrlFromState() {
  const state = store.getState();
  const stateParams = getParamsFromState(state);
  const parsedUrlParams = parseParamsFromUrl(history.location, false);

  const changedParams = getChangedParams(stateParams, parsedUrlParams, 'url');

  // No need to update the URL if nothing relevant has changed.
  if (!Object.keys(changedParams).length) {
    return;
  }

  // Construct query parameters from state.
  const newSearchParams = new URLSearchParams();

  Object.keys(stateParams).forEach(name => {
    const paramSpec = queryParams[name];
    const value = stateParams[name];
    const hasValue = value !== null && value !== undefined;
    const writeToUrl = paramSpec.writeToUrl !== false;

    if (hasValue && writeToUrl) {
      newSearchParams.set(name, value);
    }
  });

  const newPath = `${getCurrentURL().pathname}?${newSearchParams}`;

  scheduleLogSlideView(changedParams);

  // Update the URL.
  const updateHistory = deviceType.isMobile ? history.replace : history.push;
  updateHistory.call(history, newPath);
}

export function startHistory() {
  const unsubscribe = store.subscribe(updateUrlFromState);
  updateStateFromUrl(history.location);
  history.listen(updateStateFromUrl);

  return unsubscribe;
}

export default history;
